import { getOrCreateAnonymousId } from './analytics.js'
import { createSessionCache } from './session-cache.js'

export interface FlagsmithFlag {
  feature: { name: string }
  enabled: boolean
  feature_state_value: string | null
}

const FLAGSMITH_URL = 'https://edge.api.flagsmith.com/api/v1/identities/'
const DEFAULT_ENV_KEY = 'GiWB7Nt7kD6ySTn9Uo6XSH'

const load = createSessionCache<FlagsmithFlag[]>({
  windowKey: '__lyka_flagsmith__',
  sessionPrefix: '__lyka_flagsmith_session__',
})

function resolveEnvKey(envKey?: string): string {
  return envKey || import.meta.env.VITE_FLAGSMITH_ENVIRONMENT_KEY || DEFAULT_ENV_KEY
}

async function fetchFlags(envKey: string, identifier: string): Promise<FlagsmithFlag[]> {
  const url = new URL(FLAGSMITH_URL)
  url.searchParams.append('identifier', identifier)

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      Accept: 'application/json',
      'X-Environment-Key': envKey,
    },
  })

  if (!response.ok) {
    throw new Error(`Flagsmith request failed with status ${response.status}`)
  }

  const data = (await response.json()) as {
    flags?: FlagsmithFlag[]
  }

  return data.flags ?? []
}

export function getFlagsmithFlags(envKey?: string): Promise<FlagsmithFlag[]> {
  const resolvedEnvKey = resolveEnvKey(envKey)
  const identifier = getOrCreateAnonymousId()
  const cacheKey = `${resolvedEnvKey}::${identifier}`

  return load(cacheKey, () => fetchFlags(resolvedEnvKey, identifier)).catch((error) => {
    console.error('Error fetching Flagsmith flags:', error)
    return []
  })
}

export function getFlagValue(flags: ReadonlyArray<FlagsmithFlag>, name: string): string | undefined {
  const flag = flags.find((candidate) => candidate.feature.name === name && candidate.enabled)

  return flag?.feature_state_value ?? undefined
}
